import { Skeleton } from '../../../../components/ui/Skeleton/Skeleton';
import { EmptyState } from '../../../../components/ui/EmptyState/EmptyState';
import type { ActivityLogEntry } from '../../../../types/api';
import styles from './ActivityFeed.module.css';

export interface ActivityFeedProps {
  entries: ActivityLogEntry[];
  loading?: boolean;
  /** Lookup of staff_id → display name. Optional; falls back to "Staff #N". */
  staffNames?: Record<number, string>;
}

function humanise(action: string): string {
  return action.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTime(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const today = new Date();
  const sameDay =
    dt.getFullYear() === today.getFullYear() &&
    dt.getMonth() === today.getMonth() &&
    dt.getDate() === today.getDate();
  const time = dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return time;
  const date = dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  return `${date} ${time}`;
}

export function ActivityFeed({ entries, loading = false, staffNames }: ActivityFeedProps): JSX.Element {
  if (loading) {
    return (
      <div role="status" aria-label="Loading activity">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={styles.skeletonRow}>
            <Skeleton width={64} height={12} />
            <Skeleton width="80%" height={12} />
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return <EmptyState title="No recent activity" description="Audit entries will appear here once staff actions are recorded." />;
  }

  return (
    <ul className={styles.list} aria-label="Recent activity">
      {entries.map((entry) => {
        const actorLabel =
          entry.staff_id !== null
            ? staffNames?.[entry.staff_id] ?? `Staff #${entry.staff_id}`
            : entry.driver_id !== null
              ? `Driver #${entry.driver_id}`
              : 'System';
        return (
          <li key={entry.id} className={styles.item}>
            <span className={styles.time}>{formatTime(entry.created_at)}</span>
            <div className={styles.body}>
              <span className={styles.action}>{humanise(entry.action)}</span>
              <span className={styles.meta}>
                <span className={styles.actor}>{actorLabel}</span>
                {entry.entity_type ? (
                  <span className={styles.entity}>
                    {entry.entity_type}
                  </span>
                ) : null}
                {entry.details ? <span>{entry.details}</span> : null}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
